import { marketplaceAPI, pipelineAPI, pluginAPI } from './api';

type DeveloperPluginRecord = {
  id: string;
  name?: string;
  description?: string;
  status?: string;
  published?: boolean;
  enabled?: boolean;
  version?: string;
  pipelineSteps?: Array<{
    stepId: string;
    status: string;
  }>;
  type?: string;
  kind?: string;
  manifest?: {
    type?: string;
    ui?: {
      sidebar?: {
        icon?: string;
      };
    };
  };
};

type MarketplacePluginRecord = {
  id: string;
  name?: string;
  description?: string;
  status?: string;
  version?: string;
  type?: string;
  manifest?: DeveloperPluginRecord['manifest'];
};

type PipelineItem = {
  id: string;
  name?: string;
  author?: string;
  description?: string;
  status: string;
  kind: string;
  enabled?: boolean;
  version?: string;
  updatedAt?: number | null;
};

type PipelineSnapshot = {
  items: PipelineItem[];
  counts: {
    total: number;
    enabled: number;
    disabled: number;
    installed: number;
    review: number;
  };
};

export type PluginCatalogItem = {
  id: string;
  name?: string;
  author?: string;
  description?: string;
  version?: string;
  status?: string;
  publishStatus?: string;
  pipelineStatus?: string;
  type?: string;
  kind?: string;
  enabled?: boolean;
  userState?: 'enabled' | 'disabled';
  manifest?: DeveloperPluginRecord['manifest'];
  pipelineSteps?: DeveloperPluginRecord['pipelineSteps'];
  updatedAt?: number | null;
  published?: boolean;
  owned?: boolean;
  inStore?: boolean;
  inPipeline?: boolean;
};

export type PluginCatalog = {
  items: PluginCatalogItem[];
  pipelineCounts?: PipelineSnapshot['counts'];
};

export const fetchPluginCatalog = async (): Promise<PluginCatalog> => {
  const [developerResponse, marketplaceResponse, pipelineResponse, userRegistryResponse] = await Promise.all([
    pluginAPI.getMyPlugins(),
    marketplaceAPI.listPlugins(),
    pipelineAPI.getSnapshot(),
    pluginAPI.getUserRegistry(),
  ]);

  const developerPlugins =
    developerResponse?.plugins ??
    developerResponse?.data?.plugins ??
    [];
  const marketplacePlugins =
    marketplaceResponse?.data?.plugins ??
    (marketplaceResponse as { plugins?: MarketplacePluginRecord[] } | undefined)?.plugins ??
    [];
  const pipelineSnapshot =
    (pipelineResponse?.data ?? pipelineResponse) as PipelineSnapshot | null;
  const userRegistry =
    userRegistryResponse?.data?.plugins ??
    (userRegistryResponse?.plugins as Array<{ pluginId: string; state: string }> | undefined) ??
    [];

  const catalog = new Map<string, PluginCatalogItem>();

  const upsert = (id: string, patch: Partial<PluginCatalogItem>) => {
    const current = catalog.get(id) ?? { id };
    catalog.set(id, { ...current, ...patch });
  };

  developerPlugins.forEach((plugin: DeveloperPluginRecord) => {
    upsert(plugin.id, {
      name: plugin.name,
      description: plugin.description,
      status: plugin.status,
      version: plugin.version,
      published: plugin.published,
      enabled: plugin.enabled,
      pipelineSteps: plugin.pipelineSteps,
      type: plugin.type,
      kind: plugin.kind,
      manifest: plugin.manifest,
      owned: true,
    });
  });

  marketplacePlugins.forEach((plugin: MarketplacePluginRecord) => {
    upsert(plugin.id, {
      name: plugin.name,
      description: plugin.description,
      publishStatus: plugin.status,
      version: plugin.version,
      type: plugin.type,
      manifest: plugin.manifest,
      inStore: true,
    });
  });

  pipelineSnapshot?.items?.forEach((item) => {
    upsert(item.id, {
      name: item.name,
      author: item.author,
      description: item.description,
      pipelineStatus: item.status,
      kind: item.kind,
      enabled: item.enabled,
      version: item.version,
      updatedAt: item.updatedAt ?? null,
      inPipeline: true,
    });
  });

  userRegistry.forEach((entry) => {
    upsert(entry.pluginId, {
      userState: entry.state as PluginCatalogItem['userState'],
    });
  });

  return {
    items: Array.from(catalog.values()),
    pipelineCounts: pipelineSnapshot?.counts,
  };
};
